import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import './AgentsPage.css';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  const statusColor =
    normalized === 'active'
      ? '#16A34A' // green
      : normalized === 'inactive'
      ? '#DC2626' // red
      : '#CA8A04'; // yellow (unknown)
  return (
    <span
      style={{
        backgroundColor: statusColor,
        color: '#fff',
        padding: '6px 10px',
        borderRadius: '6px',
        fontWeight: '500',
        fontSize: '0.85rem',
      }}
    >
      {status || 'Unknown'}
    </span>
  );
};

const AgentsPage = () => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const openMapModal = (agent) => {
    if (agent.latitude && agent.longitude) {
      setSelectedAgent(agent);
      setIsMapOpen(true);
    } else {
      alert("Location data is not available for this agent.");
    }
  };

  const closeMapModal = () => {
    setIsMapOpen(false);
    setSelectedAgent(null);
  };

  useEffect(() => {
    const API_URL = 'https://installmentzone.com/api/agents/view_agents.php';
    axios
      .get(API_URL)
      .then((response) => {
        if (response.data && response.data.success) {
          setAgents(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch agent data.');
        }
      })
      .catch((err) => {
        setError('Could not connect to the server.');
        console.error('API Error:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const filteredAgents = agents.filter(
    (agent) =>
      (agent.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (agent.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (agent.phone_number || '').includes(searchTerm)
  );

  return (
    <div
      className="agents-page-container"
      style={{
        minHeight: '100vh',
        padding: '40px 20px',
        background: 'linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)',
        fontFamily: 'Segoe UI, sans-serif',
        color: '#fff',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollBehavior: 'smooth',
      }}
    >
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.15)',
          padding: '30px',
          borderRadius: '15px',
          maxWidth: '1000px',
          margin: '0 auto',
          boxShadow: '0 6px 15px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div className="header" style={{ textAlign: 'center', marginBottom: '25px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '600' }}>Agent Management</h1>
          <p style={{ color: '#E0E7FF' }}>
            A list of all registered agents in the system.
          </p>
        </div>

        <div
          className="toolbar"
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #93C5FD',
              width: '100%',
              maxWidth: '400px',
              outline: 'none',
              fontSize: '1rem',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid #F87171',
              borderRadius: '10px',
              padding: '15px',
              color: '#FCA5A5',
              textAlign: 'center',
              marginBottom: '20px',
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        <div
          className="table-wrapper"
          style={{ overflowX: 'auto', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '10px' }}
        >
          <table
            className="data-table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              color: '#1E293B',
            }}
          >
            <thead>
              <tr style={{ background: '#1E3A8A', color: '#fff' }}>
                <th style={{ padding: '14px', textAlign: 'left' }}>Agent Name</th>
                <th style={{ padding: '14px', textAlign: 'left' }}>Contact Info</th>
                <th style={{ padding: '14px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '14px', textAlign: 'left' }}>Approved</th>
                <th style={{ padding: '14px', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(5)].map((__, j) => (
                      <td key={j} style={{ padding: '12px', background: '#F1F5F9' }}>
                        <div
                          style={{
                            height: '14px',
                            background: '#CBD5E1',
                            borderRadius: '4px',
                            width: '70%',
                            margin: 'auto',
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredAgents.length > 0 ? (
                filteredAgents.map((agent, index) => (
                  <tr
                    key={agent.id}
                    style={{
                      background: index % 2 === 0 ? '#F8FAFC' : '#FFFFFF',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <td style={{ padding: '12px' }}>{agent.name || 'N/A'}</td>
                    <td style={{ padding: '12px' }}>
                      <div>{agent.email || 'N/A'}</div>
                      <div style={{ color: '#3B82F6' }}>{agent.phone_number || 'N/A'}</div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <StatusBadge status={agent.status} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      {agent.approved ? '✅' : '❌'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => openMapModal(agent)}
                        disabled={!agent.latitude || !agent.longitude}
                        style={{
                          background: '#1E3A8A',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '8px 14px',
                          cursor: 'pointer',
                          fontWeight: '500',
                        }}
                      >
                        View Map
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#475569',
                    }}
                  >
                    {agents.length > 0
                      ? 'No agents match your search.'
                      : 'No agents found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isMapOpen && selectedAgent && (
        <div
          className="modal-overlay"
          onClick={closeMapModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              color: '#1E293B',
              padding: '25px',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '600px',
              boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ marginBottom: '15px', textAlign: 'center' }}>
              Location for {selectedAgent.name || 'Agent'}
            </h3>
            <LoadScript googleMapsApiKey="AIzaSyAuJ-c9TtFpXgT0_z7XQsP5vMAqF252Pn0">
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '400px' }}
                center={{
                  lat: parseFloat(selectedAgent.latitude),
                  lng: parseFloat(selectedAgent.longitude),
                }}
                zoom={15}
              >
                <Marker
                  position={{
                    lat: parseFloat(selectedAgent.latitude),
                    lng: parseFloat(selectedAgent.longitude),
                  }}
                />
              </GoogleMap>
            </LoadScript>
            <button
              onClick={closeMapModal}
              style={{
                display: 'block',
                margin: '20px auto 0',
                padding: '10px 20px',
                background: '#1E3A8A',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsPage;
